/**
 * Wizard de onboarding del vecino — Sprint 18 P2.
 *
 * B1: esqueleto navegable + finalización.
 * B2: 8 pasos que reusan los forms existentes + consentimiento Ley 21.719
 * (paso 1). La asignación de recursos de la bolsa (qué llavero/control a
 * quién) NO se hace en el onboarding: se difiere a Mis Datos → Recursos de
 * tu casa (alarga el proceso y el vecino no siempre tiene los recursos a
 * mano). El onboarding sí permite SOLICITAR recursos al agregar
 * residentes/vehículos.
 *
 * El onboarding lo hace el titular OCUPANTE sobre su casa (el
 * propietario_no_residente no onboarda el hogar arrendado). "Completado"
 * = paso final alcanzado; el paso actual se guarda en localStorage. Es
 * onramp: todo se puede completar después desde "Mis Datos".
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthContext';
import { ApiError } from '../../lib/api/client';
import { finalizarWizard, registrarConsentimiento } from '../../lib/api/wizard';
import { autoeditarPersona } from '../../lib/api/me';
import {
  getCupoControlRemoto,
  listarRecursosDeLote,
  listarVehiculosDeLote,
} from '../../lib/api/lotes';
import {
  listarResidentesDeLote,
  type ResidenteConPersonaRead,
} from '../../lib/api/residentes';
import {
  TIPOS_OCUPANTE,
  TIPO_OCUPANTE_LABEL,
  confirmarTipoMiTenencia,
  type TipoOcupante,
} from '../../lib/api/tenencias';
import { PARENTESCO_LABEL } from '../../lib/labels';
import { formatRut } from '../../lib/format';
import type {
  RecursoConSuspensionRead,
  VehiculoRead,
} from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { InputCelular } from '../../components/ui/InputCelular';
import { ModalAgregarResidente } from '../../components/vecino/ModalAgregarResidente';
import { ModalVehiculo } from '../../components/vecino/ModalVehiculo';
import {
  controlesEnBolsa,
  llaverosEnBolsa,
} from '../../components/vecino/AsignarRecursoBolsa';

interface PasoDef {
  titulo: string;
  obligatorio: boolean;
}

// Estructura canónica de los 8 pasos. La asignación de recursos de la
// bolsa (qué llavero a quién, qué control a qué auto) NO se hace en el
// onboarding —alarga el proceso y el vecino no siempre tiene los recursos
// a mano—: se difiere a Mis Datos → Recursos de tu casa. El onboarding
// captura residentes/vehículos y permite SOLICITAR (no asignar) recursos.
const PASOS: PasoDef[] = [
  { titulo: 'Bienvenida y política de datos', obligatorio: true },
  { titulo: 'Confirma tus datos', obligatorio: true },
  { titulo: 'Recursos de tu casa', obligatorio: true },
  { titulo: 'Residentes', obligatorio: true },
  { titulo: 'Vehículos', obligatorio: false },
  { titulo: 'Citofonía', obligatorio: false },
  { titulo: 'Wifi', obligatorio: false },
  { titulo: 'Resumen y envío', obligatorio: true },
];

const LS_KEY = 'calbucura.wizard.paso';
const LS_CONSENTIDO = 'calbucura.wizard.consentido';
const LS_DATOS_CONFIRMADOS = 'calbucura.wizard.datos-confirmados';

function extraerError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return typeof err.body === 'object' &&
      err.body !== null &&
      'detail' in err.body
      ? String((err.body as { detail: unknown }).detail)
      : `Error ${err.status}`;
  }
  return fallback;
}

const ESTILO_INPUT =
  'w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 text-sm';

// ============================================================
// Paso 1 — Bienvenida + política de datos (Ley 21.719)
// ============================================================

function PasoBienvenida({
  consentido,
  onConsentir,
}: {
  consentido: boolean;
  onConsentir: () => Promise<void>;
}) {
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleToggle() {
    if (consentido || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      await onConsentir();
    } catch (err) {
      setError(extraerError(err, 'No se pudo registrar tu aceptación.'));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Card>
      <div className="space-y-4 text-sm text-text-secondary">
        <p>
          Te damos la bienvenida al portal del Barrio Calbucura. En unos pasos
          configuramos tu casa: tus datos, quiénes viven contigo, tus vehículos
          y los accesos (llaveros, controles, citofonía y wifi).
        </p>
        <p>
          Para continuar, necesitamos tu aceptación de la política de
          tratamiento de datos personales (Ley 21.719). Tus datos se usan solo
          para la gestión del barrio; no se venden ni se transfieren a terceros.
          Puedes leer el detalle en{' '}
          <Link to="/privacidad" className="text-orange underline">
            la política de privacidad
          </Link>
          .
        </p>
        <label className="flex items-start gap-2 text-text-primary cursor-pointer">
          <input
            type="checkbox"
            checked={consentido}
            disabled={consentido || guardando}
            onChange={handleToggle}
            className="mt-0.5 rounded-sm accent-orange"
          />
          <span>
            He leído y acepto la política de tratamiento de datos personales
            del Barrio Calbucura.
            {consentido && (
              <span className="block text-xs text-state-success mt-0.5">
                Aceptación registrada.
              </span>
            )}
          </span>
        </label>
        {error && (
          <p className="text-sm text-state-danger" role="alert">
            {error}
          </p>
        )}
        <p className="text-xs text-text-muted">
          Debes aceptar para continuar con la configuración.
        </p>
      </div>
    </Card>
  );
}

// ============================================================
// Paso 2 — Confirma tus datos
// ============================================================

function tipoOcupanteInicial(tipo: string): TipoOcupante {
  // La tenencia de onboarding siempre es ocupante (se elige por
  // TIPOS_OCUPANTE), pero validamos por defensa.
  return TIPOS_OCUPANTE.includes(tipo)
    ? (tipo as TipoOcupante)
    : 'propietario_residente';
}

function PasoDatos({
  nombre,
  rut,
  emailInicial,
  celularInicial,
  tenenciaId,
  tipoInicial,
  onConfirmado,
}: {
  nombre: string;
  rut: string;
  emailInicial: string | null;
  celularInicial: string | null;
  tenenciaId: string;
  tipoInicial: string;
  onConfirmado: () => void;
}) {
  const [email, setEmail] = useState(emailInicial ?? '');
  const [celular, setCelular] = useState(celularInicial ?? '');
  const [tipo, setTipo] = useState<TipoOcupante>(() =>
    tipoOcupanteInicial(tipoInicial),
  );
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (guardando) return;
    setGuardando(true);
    setError(null);
    setGuardado(false);
    try {
      await autoeditarPersona({
        email: email.trim() || null,
        celular: celular.trim() || null,
      });
      await confirmarTipoMiTenencia(tenenciaId, tipo);
      setGuardado(true);
      onConfirmado();
    } catch (err) {
      setError(extraerError(err, 'No se pudieron guardar tus datos.'));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-text-secondary">
          Confirma tu información de contacto y tu situación en la casa. Tu
          nombre y RUT los administra la Comisión; si hay un error, escríbeles.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="space-y-1">
            <span className="text-text-muted">Nombre</span>
            <p className="text-text-primary">{nombre}</p>
          </div>
          <div className="space-y-1">
            <span className="text-text-muted">RUT</span>
            <p className="text-text-primary">{formatRut(rut)}</p>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm text-text-secondary mb-1">
            Tu situación en esta casa
          </legend>
          {(Object.keys(TIPO_OCUPANTE_LABEL) as TipoOcupante[]).map((t) => (
            <label
              key={t}
              className="flex items-center gap-2 text-sm text-text-primary cursor-pointer"
            >
              <input
                type="radio"
                name="tipo-ocupante"
                value={t}
                checked={tipo === t}
                onChange={() => setTipo(t)}
                className="accent-orange"
              />
              <span>{TIPO_OCUPANTE_LABEL[t]}</span>
            </label>
          ))}
          <p className="text-xs text-text-muted pt-1">
            <strong className="text-text-secondary">Comodato:</strong> el dueño
            te cede la casa para que la uses, sin cobrarte arriendo o por un
            monto muy bajo. No eres dueño ni pagas arriendo de mercado.
          </p>
        </fieldset>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={255}
            className={ESTILO_INPUT}
          />
        </label>
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Celular</span>
          <InputCelular value={celular} onChange={setCelular} />
        </label>
        {error && (
          <p className="text-sm text-state-danger" role="alert">
            {error}
          </p>
        )}
        {guardado && (
          <p className="text-sm text-state-success">Datos confirmados.</p>
        )}
        <Button variant="secondary" type="submit" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Confirmar mis datos'}
        </Button>
      </form>
    </Card>
  );
}

// ============================================================
// Paso 3 — Recursos de tu casa (informativo)
// ============================================================

function PasoRecursos({ loteId }: { loteId: string }) {
  const [recursos, setRecursos] = useState<RecursoConSuspensionRead[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let activo = true;
    listarRecursosDeLote(loteId)
      .then((data) => activo && setRecursos(data))
      .catch(
        (err) =>
          activo &&
          setError(extraerError(err, 'No se pudieron cargar los recursos.')),
      );
    return () => {
      activo = false;
    };
  }, [loteId]);

  const llaveros = recursos?.filter((r) => r.tipo === 'llavero') ?? [];
  const controles = recursos?.filter((r) => r.tipo === 'control_remoto') ?? [];
  const sinAsignar = (lista: RecursoConSuspensionRead[]) =>
    lista.filter(
      (r) => r.residente_actual_id === null && r.estado === 'entregado',
    );

  return (
    <Card>
      <div className="space-y-3 text-sm text-text-secondary">
        <p>
          Estos son los recursos que la Comisión entregó a tu casa. Los que
          están sin asignar los repartirás a tus residentes (llaveros) y
          vehículos (controles) cuando quieras, desde{' '}
          <strong className="text-text-primary">
            Mis Datos → Recursos de tu casa
          </strong>
          .
        </p>
        {error && (
          <p className="text-state-danger" role="alert">
            {error}
          </p>
        )}
        {recursos === null && !error && (
          <p className="text-text-muted">Cargando…</p>
        )}
        {recursos !== null && (
          <ul className="space-y-1 text-text-primary">
            <li>
              Llaveros: <strong>{llaveros.length}</strong> ({' '}
              {sinAsignar(llaveros).length} sin asignar)
            </li>
            <li>
              Controles remotos: <strong>{controles.length}</strong> ({' '}
              {sinAsignar(controles).length} sin asignar)
            </li>
          </ul>
        )}
        {recursos !== null && recursos.length === 0 && (
          <p className="text-text-muted">
            Tu casa aún no tiene recursos registrados. La Comisión los entrega
            cuando los solicitas.
          </p>
        )}
      </div>
    </Card>
  );
}

// ============================================================
// Paso 4 — Residentes (la asignación de llaveros se difiere a Mis Datos)
// ============================================================

function PasoResidentes({ loteId }: { loteId: string }) {
  const [residentes, setResidentes] = useState<ResidenteConPersonaRead[]>([]);
  const [recursos, setRecursos] = useState<RecursoConSuspensionRead[]>([]);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [res, rec] = await Promise.all([
        listarResidentesDeLote(loteId),
        listarRecursosDeLote(loteId),
      ]);
      setResidentes(res);
      setRecursos(rec);
    } catch (err) {
      setError(extraerError(err, 'No se pudo cargar la información.'));
    } finally {
      setCargando(false);
    }
  }, [loteId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const activos = residentes.filter((r) => r.estado !== 'inactivo');
  const llaverosBolsa = llaverosEnBolsa(recursos);
  // Solo se ofrece pedir un llavero nuevo si NO hay uno de sobra en la
  // bolsa para la próxima persona. La lista de residentes YA incluye al
  // titular (Residente auto, D-P2-1), así que `activos.length` cuenta a
  // todos los que usan llavero. Si hay sobra, el modal oculta el "pedir"
  // (se asigna desde la bolsa en Mis Datos). Para llaveros el límite es
  // aprox = la gente (sin tope duro).
  const hayLlaveroDeSobra = llaverosBolsa.length > activos.length;

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-secondary">
            Quiénes viven en tu casa (no te incluyas a ti — eres el titular).
          </p>
          <Button variant="secondary" onClick={() => setModalAbierto(true)}>
            Agregar
          </Button>
        </div>

        {cargando && <p className="text-sm text-text-muted">Cargando…</p>}
        {error && (
          <p className="text-sm text-state-danger" role="alert">
            {error}
          </p>
        )}

        {!cargando && activos.length === 0 && (
          <p className="text-sm text-text-muted">
            Aún no agregaste residentes. Es opcional: si vives solo, puedes
            continuar.
          </p>
        )}

        <ul className="space-y-2">
          {activos.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between text-sm border-b border-white/[0.06] pb-2"
            >
              <span className="text-text-primary">
                {r.persona_nombre_completo}
                <span className="text-text-muted">
                  {' '}
                  · {PARENTESCO_LABEL[r.parentesco] ?? r.parentesco}
                </span>
              </span>
              <span className="text-xs text-text-muted">{r.estado}</span>
            </li>
          ))}
        </ul>

        {modalAbierto && (
          <ModalAgregarResidente
            loteId={loteId}
            hayLlaverosEnBolsa={hayLlaveroDeSobra}
            onCerrar={() => setModalAbierto(false)}
            onAgregado={() => {
              setModalAbierto(false);
              void cargar();
            }}
          />
        )}
      </div>
    </Card>
  );
}

// ============================================================
// Paso 5 — Vehículos (la asignación de controles de la bolsa se hace
// después, desde Mis Datos → Recursos de tu casa).
// ============================================================

function PasoVehiculos({ loteId }: { loteId: string }) {
  const [vehiculos, setVehiculos] = useState<VehiculoRead[]>([]);
  const [recursos, setRecursos] = useState<RecursoConSuspensionRead[]>([]);
  // Si la casa está en el tope de CR (config del barrio), NO se puede
  // ofrecer pedir uno más. El backend es la única fuente del tope + del
  // conteo (no se hardcodea ni se replica el set de estados acá).
  const [puedeSolicitarCr, setPuedeSolicitarCr] = useState(true);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [veh, rec, cupo] = await Promise.all([
        listarVehiculosDeLote(loteId),
        listarRecursosDeLote(loteId),
        getCupoControlRemoto(loteId),
      ]);
      setVehiculos(veh);
      setRecursos(rec);
      setPuedeSolicitarCr(cupo.puede_solicitar_cr);
    } catch (err) {
      setError(extraerError(err, 'No se pudo cargar la información.'));
    } finally {
      setCargando(false);
    }
  }, [loteId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const vehiculosActivos = vehiculos.filter((v) => v.estado !== 'inactivo');
  // Un CR en bolsa para asignar es uno sin vehículo todavía (D-P2-1).
  const controlesBolsa = controlesEnBolsa(recursos);
  // Se oculta "pedir control" en dos casos: (a) hay uno de sobra en la
  // bolsa para el próximo auto (se asigna desde Mis Datos), o (b) la casa
  // está en el tope de CR configurado (no se puede ofrecer algo que no se
  // puede dar; el tope lo resuelve el backend, no se hardcodea acá).
  const hayControlDeSobra = controlesBolsa.length > vehiculosActivos.length;
  const ocultarPedirControl = hayControlDeSobra || !puedeSolicitarCr;

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-secondary">
            Tus vehículos (opcional). Al agregar uno puedes pedir un control
            remoto si tu casa no tiene en la bolsa.
          </p>
          <Button variant="secondary" onClick={() => setModalAbierto(true)}>
            Agregar
          </Button>
        </div>

        {cargando && <p className="text-sm text-text-muted">Cargando…</p>}
        {error && (
          <p className="text-sm text-state-danger" role="alert">
            {error}
          </p>
        )}

        {!cargando && vehiculosActivos.length === 0 && (
          <p className="text-sm text-text-muted">
            Aún no agregaste vehículos. Puedes saltar este paso.
          </p>
        )}

        <ul className="space-y-2">
          {vehiculosActivos.map((v) => (
            <li
              key={v.id}
              className="flex items-center justify-between text-sm border-b border-white/[0.06] pb-2"
            >
              <span className="text-text-primary">
                {v.patente}
                <span className="text-text-muted">
                  {' '}
                  · {v.marca} {v.modelo}
                </span>
              </span>
              <span className="text-xs text-text-muted">{v.estado}</span>
            </li>
          ))}
        </ul>

        {modalAbierto && (
          <ModalVehiculo
            loteId={loteId}
            inicial={null}
            hayControlesEnBolsa={ocultarPedirControl}
            onCerrar={() => setModalAbierto(false)}
            onGuardado={() => {
              setModalAbierto(false);
              void cargar();
            }}
          />
        )}
      </div>
    </Card>
  );
}

// ============================================================
// Pasos 6-7 — Citofonía / Wifi (onramp: se completan en Mis Datos)
// ============================================================

function PasoAccesoOnramp({ servicio }: { servicio: 'citofonía' | 'wifi' }) {
  return (
    <Card>
      <div className="space-y-3 text-sm text-text-secondary">
        <p>
          La configuración de {servicio} se hace por residente desde{' '}
          <strong className="text-text-primary">Mis Datos</strong>, donde verás
          el detalle de cupos y slots de tu casa.
        </p>
        <p className="text-text-muted">
          Puedes continuar ahora y configurar {servicio} más tarde — el portal
          te deja completarlo cuando quieras.
        </p>
        <Link to="/vecino/mis-datos" className="text-orange underline text-sm">
          Ir a Mis Datos
        </Link>
      </div>
    </Card>
  );
}

// ============================================================
// Paso 8 — Resumen y envío
// ============================================================

function PasoResumen({ loteIdentificador }: { loteIdentificador: string }) {
  return (
    <Card>
      <div className="space-y-3 text-sm text-text-secondary">
        <p className="text-text-primary font-medium">
          Casa {loteIdentificador} — configuración lista.
        </p>
        <p>
          Al finalizar, tu configuración queda registrada. Si agregaste
          residentes, vehículos o accesos nuevos, la Comisión los revisará
          (cada uno muestra su estado "pendiente de validación"). Puedes seguir
          ajustando todo desde "Mis Datos" cuando quieras.
        </p>
      </div>
    </Card>
  );
}

export function WizardOnboarding() {
  const { me, refreshMe } = useAuth();
  const navigate = useNavigate();
  const tenencias = me?.tenencias_activas ?? [];
  const tenenciaOnboarding = tenencias.find((t) =>
    TIPOS_OCUPANTE.includes(t.tipo),
  );

  const [paso, setPaso] = useState<number>(() => {
    const guardado = Number(localStorage.getItem(LS_KEY));
    return Number.isInteger(guardado) && guardado >= 0 && guardado < PASOS.length
      ? guardado
      : 0;
  });
  const [consentido, setConsentido] = useState(
    () => localStorage.getItem(LS_CONSENTIDO) === 'true',
  );
  const [datosConfirmados, setDatosConfirmados] = useState(
    () => localStorage.getItem(LS_DATOS_CONFIRMADOS) === 'true',
  );
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function irA(n: number) {
    const destino = Math.max(0, Math.min(PASOS.length - 1, n));
    setPaso(destino);
    localStorage.setItem(LS_KEY, String(destino));
  }

  async function consentir() {
    await registrarConsentimiento();
    localStorage.setItem(LS_CONSENTIDO, 'true');
    setConsentido(true);
  }

  function marcarDatosConfirmados() {
    localStorage.setItem(LS_DATOS_CONFIRMADOS, 'true');
    setDatosConfirmados(true);
  }

  async function finalizar() {
    if (!tenenciaOnboarding || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await finalizarWizard(tenenciaOnboarding.lote_id);
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(LS_CONSENTIDO);
      localStorage.removeItem(LS_DATOS_CONFIRMADOS);
      await refreshMe?.();
      // S19-C3: el aviso "enviado a validación" ahora es una campanita
      // (NotificacionVecino onboarding_enviado creada por el backend al
      // finalizar). Ya no se pasa por navigation state.
      navigate('/vecino');
    } catch (err) {
      setError(extraerError(err, 'Error de red al finalizar.'));
      setEnviando(false);
    }
  }

  if (!me) return null;

  const persona = me.persona;
  const loteId = tenenciaOnboarding?.lote_id ?? '';
  const def = PASOS[paso];
  const esUltimo = paso === PASOS.length - 1;
  // Gates de avance: paso 0 exige consentimiento; paso 1 exige que el vecino
  // confirme sus datos + situación en la casa (botón "Confirmar mis datos").
  const avanceBloqueado =
    (paso === 0 && !consentido) || (paso === 1 && !datosConfirmados);

  function renderPaso() {
    switch (paso) {
      case 0:
        return (
          <PasoBienvenida consentido={consentido} onConsentir={consentir} />
        );
      case 1:
        return (
          <PasoDatos
            nombre={persona.nombre_completo}
            rut={persona.rut}
            emailInicial={persona.email}
            celularInicial={persona.celular}
            tenenciaId={tenenciaOnboarding?.id ?? ''}
            tipoInicial={tenenciaOnboarding?.tipo ?? 'propietario_residente'}
            onConfirmado={marcarDatosConfirmados}
          />
        );
      case 2:
        return loteId ? <PasoRecursos loteId={loteId} /> : null;
      case 3:
        return loteId ? <PasoResidentes loteId={loteId} /> : null;
      case 4:
        return loteId ? <PasoVehiculos loteId={loteId} /> : null;
      case 5:
        return <PasoAccesoOnramp servicio="citofonía" />;
      case 6:
        return <PasoAccesoOnramp servicio="wifi" />;
      case 7:
        return (
          <PasoResumen
            loteIdentificador={tenenciaOnboarding?.lote_identificador ?? ''}
          />
        );
      default:
        return null;
    }
  }

  return (
    <div className="min-h-screen bg-bg-page">
      <div className="max-w-2xl mx-auto p-4 md:p-8 space-y-4">
        <header>
          <div className="text-sm text-text-muted uppercase tracking-wide mb-1">
            Configuración inicial · Paso {paso + 1} de {PASOS.length}
          </div>
          <h1 className="text-2xl font-medium text-text-primary">
            {def.titulo}
          </h1>
          {tenenciaOnboarding && (
            <p className="text-sm text-text-secondary mt-1">
              Casa {tenenciaOnboarding.lote_identificador}
            </p>
          )}
        </header>

        <div className="flex gap-1">
          {PASOS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full ${
                i <= paso ? 'bg-orange' : 'bg-bg-sunken'
              }`}
            />
          ))}
        </div>

        {renderPaso()}

        {error && (
          <p className="text-sm text-state-danger" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-between pt-2">
          <Button
            variant="ghost"
            onClick={() => irA(paso - 1)}
            disabled={paso === 0 || enviando}
          >
            Atrás
          </Button>
          {esUltimo ? (
            <Button variant="primary" onClick={finalizar} disabled={enviando}>
              {enviando ? 'Finalizando…' : 'Finalizar onboarding'}
            </Button>
          ) : (
            <div className="flex gap-2">
              {!def.obligatorio && (
                <Button variant="ghost" onClick={() => irA(paso + 1)}>
                  Saltar
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => irA(paso + 1)}
                disabled={avanceBloqueado}
              >
                Siguiente
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
